/**
 * The page an external recipient lands on from the link in their Reg 3(2B)
 * notice.
 *
 * Public by design. The people who most need to acknowledge — an external
 * auditor, a merchant banker, counsel — have no account on this platform and
 * never will, so demanding a login would make the notice undeliverable in
 * practice. Authority comes from the single-use token in the URL.
 *
 * It shows nothing about the register. An unauthenticated page must never
 * become a way to read who received what: the recipient already knows their own
 * position from the email, and anyone else holding the link learns only that a
 * notice existed.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TbCircleCheck, TbAlertTriangle, TbLoader2 } from 'react-icons/tb';
import { sddAPI } from '../services/api';

const Shell = ({ children }) => (
  <div
    className="min-h-screen flex items-center justify-center p-6"
    style={{ background: 'var(--bg, #f4f1e9)' }}
  >
    <div className="bg-white rounded-xl border border-[#e8e2d6] p-8 max-w-lg w-full text-center">
      {children}
    </div>
  </div>
);

export default function UpsiNoticeAckPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'working' });
  // React 18 StrictMode mounts effects twice in development. The token is
  // single-use, so a second POST would report "invalid link" on a page that
  // had just succeeded.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    sddAPI
      .acknowledgeNotice_public(token)
      .then((res) => setState({ status: 'done', data: res?.data || {} }))
      .catch((error) => setState({
        status: 'error',
        message: error?.message || 'This acknowledgement link is invalid or has expired.',
      }));
  }, [token]);

  if (state.status === 'working') {
    return (
      <Shell>
        <TbLoader2 size={34} className="mx-auto mb-3 text-[#a09684] animate-spin" />
        <div className="text-[14px] text-[#7a7060]">Recording your acknowledgement…</div>
      </Shell>
    );
  }

  if (state.status === 'error') {
    return (
      <Shell>
        <TbAlertTriangle size={36} className="mx-auto mb-3 text-[#9a564a]" />
        <div className="text-[17px] font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
          This link cannot be used
        </div>
        <p className="text-[13px] text-[#7a7060] leading-relaxed">{state.message}</p>
        <p className="text-[12px] text-[#a09684] mt-4 leading-relaxed">
          It may already have been used, or it may have expired. Please reply to the compliance
          officer who sent the notice — your acknowledgement can be recorded directly.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <TbCircleCheck size={38} className="mx-auto mb-3 text-[#4a6b3f]" />
      <div className="text-[19px] font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
        Thank you{state.data.recipientName ? `, ${state.data.recipientName}` : ''}
      </div>
      <p className="text-[13px] text-[#7a7060] leading-relaxed">
        Your acknowledgement has been recorded
        {state.data.acknowledgedAt
          ? ` on ${new Date(state.data.acknowledgedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`
          : ''}
        .
      </p>
      <div className="mt-5 pt-4 border-t border-[#f0ece2] text-[12px] text-[#7a7060] leading-relaxed text-left">
        <strong>A reminder of what this means.</strong> You hold unpublished price sensitive
        information and are an insider under SEBI (Prohibition of Insider Trading) Regulations,
        2015. Until that information is published you must keep it confidential, must not pass it
        on except for a legitimate purpose, and must not deal in the company’s securities on the
        basis of it or advise anyone else to.
      </div>
    </Shell>
  );
}
